<template>
  <EmsLayout>
    <div class="bms-monitor-page" v-loading="loading">
      <!-- 顶部栏 -->
      <div class="bms-header">
        <div class="battery-info">
          <div class="battery-icon">
            <svg viewBox="0 0 64 64" width="48" height="48">
              <rect x="8" y="20" width="48" height="32" rx="4" fill="#00d4ff" opacity="0.3" />
              <rect x="14" y="26" width="36" height="20" rx="2" fill="#00d4ff" opacity="0.6" />
              <rect x="28" y="14" width="8" height="8" rx="2" fill="#00d4ff" opacity="0.5" />
            </svg>
          </div>
          <span class="battery-label">电池实时</span>
        </div>

        <!-- 状态指示器 -->
        <div class="status-indicators">
          <span
            v-for="s in statusList"
            :key="s.code"
            :class="['status-tag', s.code, s.active ? 'active' : '']">
            <i :class="s.icon"></i>
            {{ s.name }}
          </span>
        </div>

        <!-- 数据采集时间 + 曲线日期切换 -->
        <div class="data-time">
          <span class="time-label">数据采集时间</span>
          <span class="time-value">{{ dataTime || '--' }}</span>
          <span class="time-nav">
            <DateNavigator v-model="chartDate" @change="onDateChange" />
          </span>
        </div>
      </div>

      <!-- 指标卡片 -->
      <div class="metrics-grid">
        <!-- 第一行 -->
        <div class="metric-item">
          <div class="metric-label">SOC</div>
          <div class="metric-value">
            {{ metrics.soc }}
            <span class="unit">(%)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">电压</div>
          <div class="metric-value">
            {{ metrics.voltage }}
            <span class="unit">(V)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">单体最低电压</div>
          <div class="metric-value">
            {{ metrics.minVoltage }}
            <span class="unit">(V)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">单体最低温度</div>
          <div class="metric-value">
            {{ metrics.minTemp }}
            <span class="unit">(℃)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">允许充电电流</div>
          <div class="metric-value">
            {{ metrics.chargeCurrentLimit }}
            <span class="unit">(A)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">累计充电电量</div>
          <div class="metric-value">
            {{ metrics.totalCharge }}
            <span class="unit">(kWh)</span>
          </div>
        </div>

        <!-- 第二行 -->
        <div class="metric-item">
          <div class="metric-label">SOH</div>
          <div class="metric-value">
            {{ metrics.soh }}
            <span class="unit">(%)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">电流</div>
          <div class="metric-value">
            {{ metrics.current }}
            <span class="unit">(A)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">单体最高电压</div>
          <div class="metric-value">
            {{ metrics.maxVoltage }}
            <span class="unit">(V)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">单体最高温度</div>
          <div class="metric-value">
            {{ metrics.maxTemp }}
            <span class="unit">(℃)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">允许放电电流</div>
          <div class="metric-value">
            {{ metrics.dischargeCurrentLimit }}
            <span class="unit">(A)</span>
          </div>
        </div>
        <div class="metric-item">
          <div class="metric-label">累计放电电量</div>
          <div class="metric-value">
            {{ metrics.totalDischarge }}
            <span class="unit">(kWh)</span>
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div class="charts-grid">
        <div class="chart-panel">
          <div class="chart-header">
            <span class="chart-dot"></span>
            <span class="chart-title">24小时SOC曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #00d4ff"></i>
              soc(%)
            </span>
          </div>
          <div ref="socChart" class="chart-container"></div>
        </div>

        <div class="chart-panel">
          <div class="chart-header">
            <span class="chart-dot"></span>
            <span class="chart-title">24小时最值电压曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #00d4ff"></i>
              最高电压(V)
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #00ff88"></i>
              最低电压(V)
            </span>
          </div>
          <div ref="voltageChart" class="chart-container"></div>
        </div>

        <div class="chart-panel">
          <div class="chart-header">
            <span class="chart-dot"></span>
            <span class="chart-title">24小时最值温度曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #00d4ff"></i>
              最高温度(℃)
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #00ff88"></i>
              最低温度(℃)
            </span>
          </div>
          <div ref="tempChart" class="chart-container"></div>
        </div>
      </div>
    </div>
  </EmsLayout>
</template>

<script>
  import * as echarts from 'echarts';
  import dayjs from 'dayjs';
  import EmsLayout from '../layout/EmsLayout.vue';
  import DateNavigator from '../components/DateNavigator.vue';
  import { getBmsRealtimeData, queryEmsHistory } from '@/service/emsApis';
  import { formatNum, default24hLabels, singleLineOption, doubleLineOption } from '../utils';

  export default {
    name: 'BmsMonitor',
    components: {
      EmsLayout,
      DateNavigator,
    },
    data() {
      return {
        chartDate: dayjs().format('YYYY-MM-DD'),
        dataTime: '',
        statusList: [
          { code: 'running', name: '运行', icon: 'el-icon-success', active: false },
          { code: 'stop', name: '停机', icon: 'el-icon-warning-outline', active: false },
          { code: 'noDischarge', name: '禁放', icon: 'el-icon-circle-close', active: false },
          { code: 'noCharge', name: '禁充', icon: 'el-icon-circle-close', active: false },
          { code: 'noBoth', name: '禁充放', icon: 'el-icon-remove-outline', active: false },
          { code: 'alarm', name: '告警', icon: 'el-icon-bell', active: false },
          { code: 'fault', name: '故障', icon: 'el-icon-error', active: false },
          { code: 'warn', name: '预警', icon: 'el-icon-message-solid', active: false },
        ],
        metrics: {
          soc: '--',
          soh: '--',
          voltage: '--',
          current: '--',
          maxVoltage: '--',
          minVoltage: '--',
          maxTemp: '--',
          minTemp: '--',
          chargeCurrentLimit: '--',
          dischargeCurrentLimit: '--',
          totalCharge: '--',
          totalDischarge: '--',
        },
        socChartInstance: null,
        voltageChartInstance: null,
        tempChartInstance: null,
        refreshTimer: null,
        loading: false,
      };
    },
    computed: {
      stationCode() {
        return this.$store.getters.emsStationCode;
      },
    },
    watch: {
      stationCode() {
        this.fetchAllData();
      },
    },
    mounted() {
      this.$nextTick(() => {
        this.fetchAllData();
      });
      window.addEventListener('resize', this.handleResize);
      this.refreshTimer = setInterval(() => {
        this.fetchRealtimeData();
      }, 30000);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
      this.disposeCharts();
      if (this.refreshTimer) clearInterval(this.refreshTimer);
    },
    methods: {
      fetchAllData() {
        this.loading = true;
        Promise.all([this.fetchRealtimeData(), this.fetchChartData()]).finally(() => {
          this.loading = false;
        });
      },

      async fetchRealtimeData() {
        try {
          const res = await getBmsRealtimeData(this.stationCode, 0);
          if (!res) return;
          this.parseBmsData(res);
        } catch (e) {
          console.error('获取BMS实时数据失败', e);
        }
      },

      parseBmsData(data) {
        // 数据采集时间
        if (data.attm) {
          this.dataTime = dayjs(data.attm).format('YYYY-MM-DD HH:mm:ss');
        }

        // eslint-disable-next-line no-bitwise
        // 系统状态: bit0=运行/停机, bit1=禁充, bit2=禁放, bit3=告警, bit4=故障, bit5=预警
        const rawSt = data.st_sys;

        // 先重置所有状态
        this.statusList.forEach(s => {
          s.active = false;
        });

        if (rawSt != null) {
          const status = Number(rawSt);
          if (!Number.isNaN(status)) {
            const hasBit = (val, bit) => (val & bit) !== 0; // eslint-disable-line no-bitwise
            this.statusList[0].active = hasBit(status, 1); // bit0=1 运行
            this.statusList[1].active = !hasBit(status, 1); // bit0=0 停机
            this.statusList[2].active = hasBit(status, 4); // bit2 禁放
            this.statusList[3].active = hasBit(status, 2); // bit1 禁充
            this.statusList[4].active = hasBit(status, 2) && hasBit(status, 4); // 禁充放
            this.statusList[5].active = hasBit(status, 8); // bit3 告警
            this.statusList[6].active = hasBit(status, 16); // bit4 故障
            this.statusList[7].active = hasBit(status, 32); // bit5 预警
          }
        }

        // 指标数据
        this.metrics.soc = data.soc != null ? String(data.soc) : '--';
        this.metrics.soh = data.soh != null ? String(data.soh) : '--';
        this.metrics.voltage = this.formatNum(data.v);
        this.metrics.current = this.formatNum(data.c);
        this.metrics.maxVoltage = this.formatNum(data.v_max);
        this.metrics.minVoltage = this.formatNum(data.v_min);
        this.metrics.maxTemp = this.formatNum(data.t_max);
        this.metrics.minTemp = this.formatNum(data.t_min);
        this.metrics.chargeCurrentLimit = this.formatNum(data.c_o_lim);
        this.metrics.dischargeCurrentLimit = this.formatNum(data.c_i_lim);
        this.metrics.totalCharge = this.formatNum(data.q_i_sum);
        this.metrics.totalDischarge = this.formatNum(data.q_o_sum);
      },

      async fetchChartData() {
        try {
          const date = this.chartDate;
          const res = await queryEmsHistory({
            proc: 'bms_m_chart',
            param: [this.stationCode, `${date} 00:00:00`, `${date} 23:59:59`],
          });
          const list = Array.isArray(res) ? res : res?.data || [];
          this.renderAllCharts(list);
        } catch (e) {
          console.error('获取BMS曲线数据失败', e);
        }
      },

      renderAllCharts(list) {
        const xData = [];
        const socData = [];
        const vMaxData = [];
        const vMinData = [];
        const tMaxData = [];
        const tMinData = [];

        if (list.length > 0) {
          list.forEach(item => {
            xData.push(item.attm ? dayjs(item.attm).format('HH:mm') : '');
            socData.push(item.soc ?? null);
            vMaxData.push(item.v_max ?? null);
            vMinData.push(item.v_min ?? null);
            tMaxData.push(item.t_max ?? null);
            tMinData.push(item.t_min ?? null);
          });
        } else {
          xData.push(...default24hLabels());
        }

        this.renderSocChart(xData, socData);
        this.renderVoltageChart(xData, vMaxData, vMinData);
        this.renderTempChart(xData, tMaxData, tMinData);
      },

      renderSocChart(xData, yData) {
        const chartDom = this.$refs.socChart;
        if (!chartDom) return;
        if (!this.socChartInstance) {
          this.socChartInstance = echarts.init(chartDom);
        }
        const socAreaStyle = {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(0, 212, 255, 0.3)' },
              { offset: 1, color: 'rgba(0, 212, 255, 0.02)' },
            ],
          },
        };
        const option = singleLineOption('soc(%)', '#00d4ff', xData, yData, {
          areaStyle: socAreaStyle,
        });
        this.socChartInstance.setOption(option, true);
      },

      renderVoltageChart(xData, y1Data, y2Data) {
        const chartDom = this.$refs.voltageChart;
        if (!chartDom) return;
        if (!this.voltageChartInstance) {
          this.voltageChartInstance = echarts.init(chartDom);
        }
        const option = doubleLineOption(
          '最高电压(V)',
          '#00d4ff',
          '最低电压(V)',
          '#00ff88',
          xData,
          y1Data,
          y2Data
        );
        this.voltageChartInstance.setOption(option, true);
      },

      renderTempChart(xData, y1Data, y2Data) {
        const chartDom = this.$refs.tempChart;
        if (!chartDom) return;
        if (!this.tempChartInstance) {
          this.tempChartInstance = echarts.init(chartDom);
        }
        const option = doubleLineOption(
          '最高温度(℃)',
          '#00d4ff',
          '最低温度(℃)',
          '#00ff88',
          xData,
          y1Data,
          y2Data
        );
        this.tempChartInstance.setOption(option, true);
      },

      formatNum,

      onDateChange() {
        this.fetchChartData();
      },

      handleResize() {
        if (this.socChartInstance) this.socChartInstance.resize();
        if (this.voltageChartInstance) this.voltageChartInstance.resize();
        if (this.tempChartInstance) this.tempChartInstance.resize();
      },
      disposeCharts() {
        if (this.socChartInstance) this.socChartInstance.dispose();
        if (this.voltageChartInstance) this.voltageChartInstance.dispose();
        if (this.tempChartInstance) this.tempChartInstance.dispose();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../styles/variables.scss';

  .bms-monitor-page {
    width: 100%;
    height: 100%;
    background: $bg-primary;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  // 顶部栏
  .bms-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
  }

  .battery-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .battery-icon {
      color: $accent-blue;
    }

    .battery-label {
      font-size: 14px;
      color: $accent-blue;
      font-weight: 500;
    }
  }

  .status-indicators {
    display: flex;
    gap: 12px;

    .status-tag {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 12px;
      color: $text-secondary;
      background: #f6f7f8;
      border: 1px solid transparent;

      i {
        font-size: 12px;
      }

      &.active {
        color: #d0021b;
        border-color: rgba(208, 2, 27, 0.3);
        background: rgba(208, 2, 27, 0.05);
      }

      // 运行状态为绿色高亮
      &.running.active {
        color: #02c498;
        border-color: rgba(2, 196, 152, 0.3);
        background: rgba(2, 196, 152, 0.05);
      }

      // 预警状态不标红，保持普通灰色
      &.warn.active {
        color: $text-secondary;
        border-color: transparent;
        background: #f6f7f8;
      }
    }
  }

  .data-time {
    display: flex;
    align-items: center;
    gap: 8px;

    .time-label {
      font-size: 13px;
      color: $text-secondary;
    }

    .time-value {
      font-size: 13px;
      color: $accent-blue;
      font-weight: 500;
    }

    .time-nav {
      margin-left: 4px;
    }
  }

  // 指标网格
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 12px;
  }

  .metric-item {
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    padding: 16px;

    .metric-label {
      font-size: 13px;
      color: $text-secondary;
      margin-bottom: 8px;
    }

    .metric-value {
      font-size: 22px;
      font-weight: bold;
      color: $text-primary;
      font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;

      .unit {
        font-size: 12px;
        color: $text-secondary;
        font-weight: normal;
        margin-left: 4px;
      }
    }
  }

  // 图表区域
  .charts-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    min-height: 0;
  }

  .chart-panel {
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .chart-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .chart-dot {
      width: 4px;
      height: 14px;
      background: $accent-blue;
      border-radius: 2px;
    }

    .chart-title {
      font-size: 14px;
      font-weight: 600;
      color: $text-primary;
    }
  }

  .chart-legend {
    display: flex;
    gap: 16px;
    margin-bottom: 6px;
    flex-wrap: wrap;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: $text-secondary;
    }

    .legend-icon {
      width: 12px;
      height: 3px;
      border-radius: 2px;
    }
  }

  .chart-container {
    flex: 1;
    min-height: 180px;
  }
</style>
